import { Check, CircleAlert, Pencil, Plus } from 'lucide-react';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import type { Currency as ApiCurrency, wave } from '@round/api';
import type { Currency } from '@/types/creator';
import { PLATFORM_ICON } from '@/utility/campaignDisplay';
import { currencyOptions, formatMoney, supportedCurrencies } from '@/utility/money';
import CurrencySelect from '@/creator/components/CurrencySelect';
import PlatformIcon from '@/creator/components/PlatformIcon';

/**
 * A creator's own bundle offers ("3 posts for £150") on the Account page: each saved
 * offer is a summary card with an Edit action that swaps it for an inline form, and
 * "Add a bundle" opens the same form blank. Saving and removing are delegated to the
 * page, which owns the API calls and refetch; this component owns the form state and
 * client-side validation, which mirrors the CreatorBundleOffer model constraints.
 */

// Mirror the CreatorBundleOffer check constraints; the backend rejects anything outside them.
export const MIN_BUNDLE_POSTS = 2;
export const MIN_BUNDLE_DURATION_DAYS = 1;
export const MAX_BUNDLE_DURATION_DAYS = 14;

export type BundleOfferFieldErrors = Partial<Record<keyof wave.CreatorBundleOfferBody | 'non_field_errors', string>>;

export type BundleOfferSaveResult =
    | { status: 'saved' }
    | { status: 'invalid'; errors: BundleOfferFieldErrors }
    | { status: 'failed' };

type Props = {
    offers: wave.CreatorBundleOffer[];
    accounts: wave.CreatorConnectedAccount[];
    currencies: ApiCurrency[];
    // Preselected currency for a new bundle: the creator's display currency.
    defaultCurrency: Currency;
    onSave: (offerId: number | null, body: wave.CreatorBundleOfferBody) => Promise<BundleOfferSaveResult>;
    onDelete: (offerId: number) => Promise<boolean>;
};

type FormValues = {
    numPosts: string;
    price: string;
    currency: Currency;
    accountIds: number[];
    expectedDurationDays: string;
};

type FormErrors = Partial<Record<keyof FormValues | 'form', string>>;

// Django serializes a DurationField as "DD HH:MM:SS" when it spans a day or more.
export const parseExpectedDurationDays = (duration: string | null): number | null => {
    if (!duration || !duration.includes(' ')) {
        return null;
    }
    const days = Number(duration.split(' ')[0]);
    return Number.isInteger(days) ? days : null;
};

export const formatExpectedDurationDays = (days: number): string => `${days} 00:00:00`;

const isSupportedCurrency = (value: string | undefined): value is Currency =>
    value !== undefined && supportedCurrencies.includes(value as Currency);

const validate = (values: FormValues, canPickAccounts: boolean): FormErrors => {
    const errors: FormErrors = {};
    const numPosts = Number(values.numPosts);
    if (values.numPosts.trim() === '' || !Number.isInteger(numPosts) || numPosts < MIN_BUNDLE_POSTS) {
        errors.numPosts = `A bundle needs at least ${MIN_BUNDLE_POSTS} posts.`;
    }
    const price = Number(values.price);
    if (values.price.trim() === '' || !Number.isFinite(price) || price <= 0) {
        errors.price = 'Enter a price for this bundle.';
    }
    if (values.expectedDurationDays.trim() !== '') {
        const days = Number(values.expectedDurationDays);
        if (!Number.isInteger(days) || days < MIN_BUNDLE_DURATION_DAYS || days > MAX_BUNDLE_DURATION_DAYS) {
            errors.expectedDurationDays = `Enter between ${MIN_BUNDLE_DURATION_DAYS} and ${MAX_BUNDLE_DURATION_DAYS} days.`;
        }
    }
    if (canPickAccounts && values.accountIds.length === 0) {
        errors.accountIds = 'Pick at least one account.';
    }
    return errors;
};

const SERVER_FIELD: Record<keyof BundleOfferFieldErrors, keyof FormErrors> = {
    num_posts: 'numPosts',
    price: 'price',
    currency: 'currency',
    accounts: 'accountIds',
    expected_duration: 'expectedDurationDays',
    non_field_errors: 'form',
};

type FormProps = {
    initialValues: FormValues;
    accounts: wave.CreatorConnectedAccount[];
    currencies: ApiCurrency[];
    submitLabel: string;
    onSubmit: (body: wave.CreatorBundleOfferBody) => Promise<BundleOfferSaveResult>;
    onCancel: () => void;
    onDelete?: () => Promise<boolean>;
};

const BundleOfferForm = ({
    initialValues,
    accounts,
    currencies,
    submitLabel,
    onSubmit,
    onCancel,
    onDelete,
}: FormProps) => {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const id = useId();
    const canPickAccounts = accounts.length > 1;
    const busy = isSaving || isDeleting;

    const setValue = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
        setValues((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: undefined }));
    };

    const toggleAccount = (accountId: number) =>
        setValue(
            'accountIds',
            values.accountIds.includes(accountId)
                ? values.accountIds.filter((value) => value !== accountId)
                : [...values.accountIds, accountId]
        );

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nextErrors = validate(values, canPickAccounts);
        const currency = currencies.find((option) => option.name === values.currency);
        if (!currency) {
            nextErrors.currency = 'Pick a currency.';
        }
        if (Object.keys(nextErrors).length > 0 || !currency) {
            setErrors(nextErrors);
            return;
        }
        setIsSaving(true);
        try {
            const days = values.expectedDurationDays.trim();
            const result = await onSubmit({
                num_posts: Number(values.numPosts),
                price: Number(values.price).toFixed(2),
                currency: currency.id,
                // With a single account the picker is hidden and the bundle covers it.
                accounts: canPickAccounts ? values.accountIds : accounts.map((account) => account.id),
                expected_duration: days === '' ? null : formatExpectedDurationDays(Number(days)),
            });
            if (result.status === 'invalid') {
                setErrors(
                    Object.fromEntries(
                        Object.entries(result.errors)
                            .filter(([field]) => field in SERVER_FIELD)
                            .map(([field, message]) => [SERVER_FIELD[field as keyof BundleOfferFieldErrors], message])
                    )
                );
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete) {
            return;
        }
        setIsDeleting(true);
        try {
            await onDelete();
        } finally {
            setIsDeleting(false);
        }
    };

    const fieldClass = (field: keyof FormValues) => (errors[field] ? 'field field--error' : 'field');
    const fieldError = (field: keyof FormErrors) =>
        errors[field] ? (
            <small id={`${id}-${field}-error`} role="alert">
                <CircleAlert size={13} /> <span>{errors[field]}</span>
            </small>
        ) : null;

    return (
        <form className="bundle-offer-form" onSubmit={handleSubmit} noValidate>
            <div className="bundle-offer-form__row">
                <label className={fieldClass('numPosts')}>
                    <span>Number of posts</span>
                    <div>
                        <input
                            type="number"
                            aria-label="Number of posts"
                            inputMode="numeric"
                            min={MIN_BUNDLE_POSTS}
                            step="1"
                            aria-invalid={Boolean(errors.numPosts)}
                            aria-describedby={errors.numPosts ? `${id}-numPosts-error` : undefined}
                            value={values.numPosts}
                            disabled={busy}
                            onChange={(event) => setValue('numPosts', event.target.value)}
                        />
                    </div>
                    {fieldError('numPosts')}
                </label>
                <label className={fieldClass('price')}>
                    <span>Price for the whole bundle</span>
                    <div>
                        <input
                            type="number"
                            aria-label="Price for the whole bundle"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            aria-invalid={Boolean(errors.price)}
                            aria-describedby={errors.price ? `${id}-price-error` : undefined}
                            value={values.price}
                            disabled={busy}
                            onChange={(event) => setValue('price', event.target.value)}
                        />
                    </div>
                    {fieldError('price')}
                </label>
                <div className={fieldClass('currency')}>
                    <span id={`${id}-currency-label`}>Currency</span>
                    <CurrencySelect
                        labelId={`${id}-currency-label`}
                        value={values.currency}
                        options={currencyOptions}
                        onChange={(currency) => setValue('currency', currency)}
                        disabled={busy}
                    />
                    {fieldError('currency')}
                </div>
            </div>
            <label className={fieldClass('expectedDurationDays')}>
                <span>Days needed to deliver it (optional)</span>
                <div>
                    <input
                        type="number"
                        aria-label="Days needed to deliver it (optional)"
                        inputMode="numeric"
                        min={MIN_BUNDLE_DURATION_DAYS}
                        max={MAX_BUNDLE_DURATION_DAYS}
                        step="1"
                        aria-invalid={Boolean(errors.expectedDurationDays)}
                        aria-describedby={errors.expectedDurationDays ? `${id}-expectedDurationDays-error` : undefined}
                        value={values.expectedDurationDays}
                        disabled={busy}
                        onChange={(event) => setValue('expectedDurationDays', event.target.value)}
                    />
                </div>
                {fieldError('expectedDurationDays')}
            </label>
            {canPickAccounts && (
                <fieldset className={`bundle-offer-form__accounts ${errors.accountIds ? 'field--error' : ''}`}>
                    <legend>Accounts this bundle covers</legend>
                    {accounts.map((account) => (
                        <label className="checkbox-row" key={account.id}>
                            <input
                                type="checkbox"
                                checked={values.accountIds.includes(account.id)}
                                disabled={busy}
                                onChange={() => toggleAccount(account.id)}
                            />
                            <span>
                                <Check size={13} />
                            </span>
                            <PlatformIcon platform={PLATFORM_ICON[account.platform]} size={13} />
                            {account.handle}
                        </label>
                    ))}
                    {fieldError('accountIds')}
                </fieldset>
            )}
            {fieldError('form')}
            <div className="bundle-offer-form__actions">
                {onDelete && isConfirmingDelete ? (
                    <>
                        <span>Remove this bundle?</span>
                        <button className="button button--danger" type="button" disabled={busy} onClick={handleDelete}>
                            {isDeleting ? 'Removing…' : 'Yes, remove'}
                        </button>
                        <button
                            className="button button--secondary"
                            type="button"
                            disabled={busy}
                            onClick={() => setIsConfirmingDelete(false)}
                        >
                            Keep
                        </button>
                    </>
                ) : (
                    <>
                        <button className="button button--primary" type="submit" disabled={busy}>
                            {isSaving ? 'Saving…' : submitLabel}
                        </button>
                        <button className="button button--secondary" type="button" disabled={busy} onClick={onCancel}>
                            Cancel
                        </button>
                        {onDelete && (
                            <button
                                className="text-button text-button--danger"
                                type="button"
                                disabled={busy}
                                onClick={() => setIsConfirmingDelete(true)}
                            >
                                Remove
                            </button>
                        )}
                    </>
                )}
            </div>
        </form>
    );
};

const BundleOffers = ({ offers, accounts, currencies, defaultCurrency, onSave, onDelete }: Props) => {
    const [editing, setEditing] = useState<number | 'new' | null>(null);

    const currencyCode = (currencyId: number): Currency => {
        const name = currencies.find((currency) => currency.id === currencyId)?.name;
        return isSupportedCurrency(name) ? name : defaultCurrency;
    };

    const toFormValues = (offer: wave.CreatorBundleOffer): FormValues => ({
        numPosts: String(offer.num_posts),
        price: offer.price,
        currency: currencyCode(offer.currency),
        accountIds: offer.accounts,
        expectedDurationDays: String(parseExpectedDurationDays(offer.expected_duration) ?? ''),
    });

    const handleSave = async (offerId: number | null, body: wave.CreatorBundleOfferBody) => {
        const result = await onSave(offerId, body);
        if (result.status === 'saved') {
            setEditing(null);
        }
        return result;
    };

    const handleDelete = async (offerId: number) => {
        const removed = await onDelete(offerId);
        if (removed) {
            setEditing(null);
        }
        return removed;
    };

    return (
        <div className="bundle-offers">
            {offers.length === 0 && editing !== 'new' && <p>You haven’t added any bundles yet.</p>}
            {offers.length > 0 && (
                <div className="bundle-offer-list">
                    {offers.map((offer) => {
                        if (editing === offer.id) {
                            return (
                                <div className="bundle-offer bundle-offer--editing" key={offer.id}>
                                    <BundleOfferForm
                                        initialValues={toFormValues(offer)}
                                        accounts={accounts}
                                        currencies={currencies}
                                        submitLabel="Save bundle"
                                        onSubmit={(body) => handleSave(offer.id, body)}
                                        onCancel={() => setEditing(null)}
                                        onDelete={() => handleDelete(offer.id)}
                                    />
                                </div>
                            );
                        }
                        const symbol = currencies.find((currency) => currency.id === offer.currency)?.symbol ?? '';
                        const offerAccounts = accounts.filter((account) => offer.accounts.includes(account.id));
                        const coversEveryAccount = accounts.length > 0 && offerAccounts.length === accounts.length;
                        const days = parseExpectedDurationDays(offer.expected_duration);
                        return (
                            <div className="bundle-offer" key={offer.id}>
                                <div className="bundle-offer__summary">
                                    <b>
                                        {offer.num_posts} posts for {formatMoney(symbol, offer.price)}
                                    </b>
                                    <span>
                                        {days === null
                                            ? 'No time estimate'
                                            : `~ ${days} ${days === 1 ? 'day' : 'days'}`}
                                    </span>
                                </div>
                                <div className="bundle-offer__accounts">
                                    {coversEveryAccount || offerAccounts.length === 0
                                        ? 'All your accounts'
                                        : offerAccounts.map((account) => (
                                              <span key={account.id}>
                                                  <PlatformIcon platform={PLATFORM_ICON[account.platform]} size={12} />
                                                  {account.handle}
                                              </span>
                                          ))}
                                </div>
                                <button
                                    className="text-button"
                                    type="button"
                                    disabled={editing !== null}
                                    onClick={() => setEditing(offer.id)}
                                    aria-label={`Edit ${offer.num_posts} posts for ${formatMoney(symbol, offer.price)}`}
                                >
                                    <Pencil size={13} /> <span>Edit</span>
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
            {editing === 'new' ? (
                <div className="bundle-offer bundle-offer--editing">
                    <BundleOfferForm
                        initialValues={{
                            numPosts: '',
                            price: '',
                            currency: defaultCurrency,
                            accountIds: accounts.map((account) => account.id),
                            expectedDurationDays: '',
                        }}
                        accounts={accounts}
                        currencies={currencies}
                        submitLabel="Add bundle"
                        onSubmit={(body) => handleSave(null, body)}
                        onCancel={() => setEditing(null)}
                    />
                </div>
            ) : (
                <button
                    className="button button--secondary"
                    type="button"
                    disabled={editing !== null}
                    onClick={() => setEditing('new')}
                >
                    <Plus size={15} /> <span>Add a bundle</span>
                </button>
            )}
        </div>
    );
};

export default BundleOffers;
